import { useState, type ReactNode } from 'react';
import {
  DatePicker,
  FullScreenSheet,
  Switch,
  TimePicker,
} from '@/components/ui';
import { toIsoWithTz } from '@/lib/tz';
import { REVIEW_COPY } from '@/pages/review/reviewCopy';
import {
  DATE_ORDER_MESSAGE,
  TIME_ORDER_MESSAGE,
  useDateTimeFields,
} from '@/pages/review/useDateTimeFields';

export interface DateTimeSheetValue {
  /** ISO with the zone's offset; a date-only pick is local noon. */
  start: string;
  end: string | null;
  /** No hour chosen → `is_time_unknown`. */
  allDay: boolean;
}

export interface DateTimeSheetProps {
  open: boolean;
  /** The item's title, shown under the 'Date & Time' header. */
  title: string;
  initial: { start: string | null; end: string | null; allDay: boolean };
  /** The resolved IANA zone the pickers read and write in. */
  timezone: string;
  onDone(value: DateTimeSheetValue): void;
  onClose(): void;
  /** A save is in flight: Done shows it and stays put. */
  busy?: boolean;
}

/**
 * Review v0 "Add date" (YAR-554): the All-day / Start / End slice of
 * today's item form, alone on a full-screen sheet. Mounted only while open,
 * so each opening starts from `initial`.
 */
export function DateTimeSheet(props: DateTimeSheetProps) {
  if (!props.open) return null;
  return <DateTimeSheetBody {...props} />;
}

function DateTimeSheetBody({
  title,
  initial,
  timezone,
  onDone,
  onClose,
  busy = false,
}: DateTimeSheetProps) {
  const f = useDateTimeFields(initial, timezone);
  const [showOrder, setShowOrder] = useState(false);
  const order = f.orderErrors();
  const orderMessage = order.dateOrder
    ? DATE_ORDER_MESSAGE
    : order.timeOrder
      ? TIME_ORDER_MESSAGE
      : null;

  const done = () => {
    if (!f.startDate) return;
    if (orderMessage) {
      setShowOrder(true);
      return;
    }
    // No start hour is a date-only value (D7: an event with a date and no
    // time does not block). Writing it as timed would turn the noon anchor
    // into an hour the parent never chose (YAR-378).
    const allDay = f.allDay || !f.startTime;
    const start = toIsoWithTz(
      allDay ? f.startDate : `${f.startDate}T${f.startTime}`,
      timezone,
    );
    let end: string | null = null;
    if (allDay) {
      end = f.endDate ? toIsoWithTz(f.endDate, timezone) : null;
    } else if (f.endTime) {
      end = toIsoWithTz(`${f.endDate || f.startDate}T${f.endTime}`, timezone);
    }
    onDone({ start, end, allDay });
  };

  const field = (label: string, children: ReactNode) => (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="flex gap-2">{children}</div>
    </div>
  );
  const orderShown = showOrder && !!orderMessage;

  return (
    <FullScreenSheet
      title={REVIEW_COPY.dateTimeTitle}
      onClose={onClose}
      leading="close"
      footer={
        <button
          type="button"
          onClick={done}
          disabled={!f.startDate || busy}
          aria-busy={busy}
          className="flex min-h-11 w-full cursor-pointer items-center justify-center rounded-md bg-primary px-4 py-2 text-base font-semibold text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-disabled-text"
        >
          {REVIEW_COPY.dateTimeDone}
        </button>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="m-0 text-base font-semibold text-foreground break-words">
          {title}
        </p>

        <div className="flex flex-col gap-3 rounded-md border border-border-strong p-3">
          <div className="flex items-center gap-4">
            <Switch
              size="lg"
              checked={f.allDay}
              aria-label={REVIEW_COPY.dateTimeAllDay}
              onChange={() => {
                f.setAllDay((prev) => !prev);
                setShowOrder(false);
              }}
            />
            <span className="text-sm font-medium text-foreground">
              {REVIEW_COPY.dateTimeAllDay}
            </span>
          </div>

          {field(
            REVIEW_COPY.dateTimeStart,
            <>
              {/* min-w-0 lets date + time shrink on narrow viewports. */}
              <div className="flex-1 min-w-0">
                <DatePicker
                  value={f.startDate}
                  onChange={(next) => {
                    f.changeStartDate(next);
                    setShowOrder(false);
                  }}
                  placeholder="Select date"
                />
              </div>
              {!f.allDay && (
                <div className="flex-1 min-w-0">
                  <TimePicker
                    value={f.startTime}
                    onChange={(next) => {
                      f.setStartTime(next);
                      setShowOrder(false);
                    }}
                  />
                </div>
              )}
            </>,
          )}

          <div className="flex flex-col gap-2">
            {field(
              REVIEW_COPY.dateTimeEnd,
              <>
                <div className="flex-1 min-w-0">
                  <DatePicker
                    value={f.endDate}
                    onChange={(next) => {
                      f.setEndDate(next);
                      setShowOrder(false);
                    }}
                    placeholder="Select date"
                    error={orderShown && order.dateOrder}
                  />
                </div>
                {!f.allDay && (
                  <div className="flex-1 min-w-0">
                    <TimePicker
                      value={f.endTime}
                      onChange={(next) => {
                        f.setEndTime(next);
                        setShowOrder(false);
                      }}
                      error={orderShown && order.timeOrder}
                    />
                  </div>
                )}
              </>,
            )}
            {orderShown && (
              <p
                role="alert"
                className="m-0 text-sm text-text-error"
              >
                {orderMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    </FullScreenSheet>
  );
}
